import React, { useState } from "react";
import { Star } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

/** Overlapping avatar stack + "N interested" used on the event detail card and directory cards. */
export function InterestedAvatars({ avatars = [], count = 0, className = "" }) {
  if (!count) return null;
  const shown = avatars.slice(0, 5);
  const extra = count - shown.length;
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="event-interested-avatars">
      <div className="flex -space-x-2">
        {shown.map((a, i) => (
          <Avatar key={i} className="h-7 w-7 border-2 border-card">
            {a.headshot_url && <AvatarImage src={fileUrl(a.headshot_url)} alt="" />}
            <AvatarFallback className="bg-teal-50 text-[10px] text-teal-700">{(a.display_name || "?")[0]}</AvatarFallback>
          </Avatar>
        ))}
        {extra > 0 && (
          <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-card bg-secondary text-[10px] font-[640] text-foreground">+{extra}</div>
        )}
      </div>
      <span className="text-xs text-muted-foreground">{count} interested</span>
    </div>
  );
}

/** Toggle "I'm interested" on an event, with a live overlapping-avatars count. */
export function InterestedButton({ eventId, preview, className = "" }) {
  const [state, setState] = useState(preview || { count: 0, avatars: [], interested: false });
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/interested`, {});
      setState((s) => ({ ...s, interested: data.interested, count: data.count }));
    } catch (e) { /* ignore */ } finally { setBusy(false); }
  };

  return (
    <div className={className}>
      <InterestedAvatars avatars={state.avatars} count={state.count} className="mb-3 justify-center" />
      <Button
        onClick={toggle}
        disabled={busy}
        variant={state.interested ? "default" : "secondary"}
        className="w-full"
        data-testid="event-interested-button"
      >
        <Star className={`mr-2 h-4 w-4 ${state.interested ? "fill-current" : ""}`} />
        {state.interested ? "Interested" : "I'm interested"}
      </Button>
    </div>
  );
}
